import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { GET_ALL_TEAMS } from "../graphql/query";
import { getCompanyId } from "../redux/slices/companySlice";

const parseLocationId = () => {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem("currentLocationId") || "";
  // "All locations" is encoded as a comma-separated list; we still pass the
  // first valid id so the team list is at least scoped to one location.
  // Callers that need stricter behavior should gate on their own logic.
  if (!raw) return null;
  const first = raw.split(",")[0];
  const n = Number(first);
  return Number.isFinite(n) ? n : null;
};

/**
 * Fetches the team list for the current company + location.
 *
 * Reads `companyId` from the `company` redux slice and `locationId` from
 * sessionStorage so callers don't need to thread these through. Refetches
 * automatically when either changes (companyId via store updates, locationId
 * via the `currentLocationId` value being passed as a query variable).
 *
 * @param {{ skip?: boolean }} [options] - Pass `skip: true` to suppress the
 *   query entirely (e.g. when the caller already has the list and wants to
 *   no-op this hook).
 * @returns {{ teams: Array, activeTeams: Array, loading: boolean }}
 */
const useTeams = ({ skip = false } = {}) => {
  const companyId = useSelector(getCompanyId);
  const locationId = parseLocationId();

  const { data, loading } = useQuery(GET_ALL_TEAMS, {
    variables: { companyId, locationId },
    skip: skip || !companyId,
    fetchPolicy: "cache-and-network",
  });

  // Full team list as returned by the API for the current company/location.
  const teams = useMemo(() => data?.getTeams?.data || [], [data]);

  // Active subset — most dashboard filters should use this so deactivated
  // teams never appear as a selectable option. Callers that genuinely need
  // every team (settings/admin views, audits) can use `teams` instead.
  const activeTeams = useMemo(
    () => teams.filter(t => (t?.status || "").toUpperCase() === "ACTIVE"),
    [teams]
  );

  return { teams, activeTeams, loading };
};

export default useTeams;
